import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Panel } from "./console";
import { DonutChart, TrendChart, type DonutSlice, type TrendSeries } from "./charts";
import { CalendarIcon, RefreshCw } from "./icons";
import { fullLAK, num } from "../lib/format";
import { STATUS_LABEL_LO } from "../lib/requestStatus";
import type { StatusCount } from "../lib/api";
import {
  STATUS_ORDER, bucketDaily, statusColor, type DailyRow,
} from "../lib/dashboard-data";
import { MAX_DAYS, PRESETS, dm, dmy, laoDay, shiftDay, type Range } from "../lib/stats-range";

/**
 * ชิ้นส่วนพื้นฐานของ Dashboard (แถบช่วงเวลา · ปุ่มโหลดใหม่ · แนวโน้ม · สถานะ) — ชิ้นส่วนตาม
 * mockup v3 อยู่ที่ `dx.tsx`
 */

/** ปุ่มโหลดใหม่ + เวลาที่อัปเดตล่าสุด */
export function RefreshButton({ updatedAt, busy }: { updatedAt: number; busy: boolean }) {
  const queryClient = useQueryClient();
  const time = updatedAt
    ? new Date(updatedAt).toLocaleTimeString("lo-LA", { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;
  return (
    <button
      type="button"
      className="btn-secondary dash-refresh"
      onClick={() => void queryClient.invalidateQueries({ queryKey: ["stats"] })}
      disabled={busy}
    >
      <span className={busy ? "dash-refresh__icon is-spinning" : "dash-refresh__icon"}>
        <RefreshCw size={14} />
      </span>
      {time ? (
        <span>
          ອັບເດດ <span className="num">{time}</span>
        </span>
      ) : (
        <span>ໂຫຼດໃໝ່</span>
      )}
    </button>
  );
}

/** แถบเลือกช่วงเวลา: ปุ่มด่วน + กำหนดวันที่เอง */
export function RangeBar({ range, text, onChange }: { range: Range; text: string; onChange: (r: Range) => void }) {
  // วันนี้คิดครั้งเดียวตอนเปิดจอ (ห้ามเรียก Date.now() ระหว่าง render)
  const [today] = useState(() => laoDay(Date.now()));
  const [customOpen, setCustomOpen] = useState(range.kind === "custom");
  const [from, setFrom] = useState(range.kind === "custom" ? range.from : shiftDay(today, -29));
  const [to, setTo] = useState(range.kind === "custom" ? range.to : today);
  const span = from && to ? Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1 : 0;
  const invalid =
    !from || !to || from > to
      ? "ວັນທີເລີ່ມຕ້ອງບໍ່ຫຼັງວັນທີສຸດທ້າຍ"
      : span > MAX_DAYS
        ? `ເລືອກໄດ້ບໍ່ເກີນ ${String(MAX_DAYS)} ວັນ`
        : null;

  return (
    <div className="range-bar">
      <div className="seg" role="group" aria-label="ຊ່ວງເວລາ">
        <button
          type="button"
          className={!customOpen && range.kind === "today" ? "seg__btn is-active" : "seg__btn"}
          onClick={() => {
            setCustomOpen(false);
            onChange({ kind: "today" });
          }}
        >
          ມື້ນີ້
        </button>
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            className={!customOpen && range.kind === "days" && range.days === p.days ? "seg__btn is-active" : "seg__btn"}
            onClick={() => {
              setCustomOpen(false);
              onChange({ kind: "days", days: p.days });
            }}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          className={!customOpen && range.kind === "ytd" ? "seg__btn is-active" : "seg__btn"}
          onClick={() => {
            setCustomOpen(false);
            onChange({ kind: "ytd" });
          }}
        >
          ນັບແຕ່ຕົ້ນປີ (YTD)
        </button>
        <button type="button" className={customOpen ? "seg__btn is-active" : "seg__btn"} onClick={() => setCustomOpen(true)}>
          <CalendarIcon size={14} /> ເລືອກຊ່ວງວັນທີ
        </button>
      </div>

      {customOpen ? (
        <form
          className="range-bar__custom"
          onSubmit={(e) => {
            e.preventDefault();
            if (!invalid) onChange({ kind: "custom", from, to });
          }}
        >
          <label className="range-bar__date">
            <span>ຈາກ</span>
            <input type="date" className="input-field num" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="range-bar__date">
            <span>ຫາ</span>
            <input type="date" className="input-field num" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="submit" className="btn-secondary" disabled={invalid !== null}>
            ນຳໃຊ້
          </button>
          {invalid ? <span className="range-bar__error">{invalid}</span> : null}
        </form>
      ) : null}

      <span className="range-bar__text">
        <CalendarIcon size={14} />
        <span className="num">{text}</span>
      </span>
    </div>
  );
}

/** กราฟแนวโน้ม สลับคำร้อง/ค่าธรรมเนียม */
export function TrendPanel({
  rows,
  weekly,
  pending,
  fees = true,
}: {
  rows: DailyRow[];
  weekly: boolean;
  pending: boolean;
  /** false = ไม่มีสิทธิ์ดูการเงินรวม (DSH-04) — ไม่มีสวิตช์ค่าธรรมเนียม */
  fees?: boolean;
}) {
  const [picked, setMetric] = useState<"requests" | "fees">("requests");
  const metric = fees ? picked : "requests";
  const buckets = bucketDaily(rows, weekly);
  const series: TrendSeries[] =
    metric === "requests"
      ? [
          { label: "ຍື່ນຄຳຮ້ອງ", color: "var(--navy-600)", values: buckets.map((b) => b.row.submitted) },
          { label: "ອອກເອກະສານ", color: "var(--success)", values: buckets.map((b) => b.row.issued) },
        ]
      : [{ label: "ຄ່າທຳນຽມທີ່ຊຳລະ", color: "var(--gold-600)", values: buckets.map((b) => b.row.fee_lak) }];

  return (
    <Panel title="ແນວໂນ້ມ">
      <div className="dash-panel-head">
        <p className="dash-panel-head__note">
          {weekly ? "ລວມເປັນລາຍອາທິດ (ຊ່ວງຍາວກວ່າ 3 ເດືອນ)" : "ລາຍວັນ ຕາມເວລາລາວ"} · ຊີ້ເມົ້າເພື່ອເບິ່ງຕົວເລກ
        </p>
        {fees ? (
          <Segmented
            label="ເລືອກຂໍ້ມູນ"
            value={metric}
            onChange={setMetric}
            options={[
              { value: "requests", label: "ຄຳຮ້ອງ" },
              { value: "fees", label: "ຄ່າທຳນຽມ" },
            ]}
          />
        ) : null}
      </div>
      <TrendChart
        series={series}
        labels={buckets.map((b) => dm(b.start))}
        tooltipTitle={(i) => {
          const b = buckets[i];
          if (!b) return "";
          return b.start === b.end ? dmy(b.start) : `${dmy(b.start)} – ${dmy(b.end)}`;
        }}
        integer={metric === "requests"}
        format={metric === "requests" ? (v) => `${num(v)} ໃບ` : fullLAK}
        title="ກຣາຟແນວໂນ້ມ"
        emptyLabel={
          pending ? "ກຳລັງໂຫຼດ…" : metric === "requests" ? "ຍັງບໍ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້" : "ຍັງບໍ່ມີການຊຳລະໃນຊ່ວງນີ້"
        }
      />
    </Panel>
  );
}

/** ปุ่มกลุ่มเลือกได้ทีละอัน */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? "seg__btn is-active" : "seg__btn"}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** โดนัทสถานะ ณ ตอนนี้ (ไม่ผูกช่วงเวลา) */
export function StatusPanel({ items, pending }: { items: StatusCount[]; pending: boolean }) {
  const slices: DonutSlice[] = [...items]
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
    .filter((c) => c.count > 0)
    .map((c) => ({ label: STATUS_LABEL_LO[c.status], value: c.count, color: statusColor(c.status) }));
  return (
    <Panel title="ສະຖານະຄຳຮ້ອງຕອນນີ້" note="ນັບທຸກຄຳຮ້ອງໃນເຂດ ບໍ່ຂຶ້ນກັບຊ່ວງວັນທີທີ່ເລືອກ">
      <DonutChart
        slices={slices}
        title="ອັດຕາສ່ວນຄຳຮ້ອງແຍກຕາມສະຖານະ"
        totalLabel="ຄຳຮ້ອງທັງໝົດ"
        emptyLabel={pending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີຄຳຮ້ອງໃນເຂດນີ້"}
      />
    </Panel>
  );
}

/** ที่มาของตัวเลข — ค่าธรรมเนียมมาจาก request_payments: Lao QR ที่ธนาคารยืนยัน (ADR-0018) หรือบันทึกที่ห้องการ */
export function DashFootnote() {
  return (
    <p className="dash-footnote">
      ຈຳນວນຄຳຮ້ອງນັບຕາມວັນທີ່ຍື່ນ · ຄ່າທຳນຽມນັບຕາມວັນທີ່ຊຳລະ ຈາກການຈ່າຍຜ່ານ Lao QR ທີ່ທະນາຄານຢືນຢັນແລ້ວ
      ແລະ ການບັນທຶກຊຳລະຢູ່ຫ້ອງການ
    </p>
  );
}
